import { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { t } from "@/i18n-js/instance";
import { useDesignConfigData } from "@/react/components/BrandedAppsBuilderUI/hooks/useDesignConfigData";
import { PageMetaData } from "@/react/components/Layout/PageMetaData";
import { SkeletonLoader } from "@/react/components/shared/uikit/SkeletonLoader";
import { Typography } from "@/react/components/shared/uikit/Typography";
import type { MobileHomeSegment } from "@/react/types/MobileHomeSegment";
import { SetLayoutConfig } from "../../Layout/SetLayoutConfig";
import { MobileHomeSegmentsProvider } from "../../MobileApp/MobileHomeSegmentsContext";
import { useMobileAppSettings } from "../../MobileApp/hooks/useMobileAppSettings";
import { BrandingSettingsForm } from "../BrandingSettingsForm";
import { FormDivider } from "../FormDivider";
import { MobileNavigationForm } from "../MobileNavigationForm";
import type { CommunitySettings } from "../index";

export interface InlineAttachment {
  signed_id: string;
}
export interface BrandingSettings {
  brand_color_dark_mode?: string;
  brand_color_light_mode?: string;
  button_text_color_dark?: string;
  button_text_color_light?: string;
  logo_dark?: string;
  logo_light?: string;
}

const getInlineAttachmentData = (
  sgid?: string,
  inlineAttachments?: InlineAttachment[],
) => {
  if (!sgid || !inlineAttachments) {
    return null;
  }

  const inlineAttachment = inlineAttachments.find(
    attachment => attachment.signed_id === sgid,
  );

  if (!inlineAttachment) {
    return null;
  }

  return inlineAttachment;
};

export const PlusLiteDashboard = () => {
  const pageTitle = t("settings_nav_v3.branded_apps_dashboard.title");

  const { defaultValues, mobileHomeSegments, mutate, isSaving, isLoading } =
    useMobileAppSettings();

  const [orderedMobileHomeSegments, setOrderedMobileHomeSegments] = useState<
    MobileHomeSegment[]
  >([]);

  useEffect(() => {
    if (mobileHomeSegments && mobileHomeSegments.length > 0) {
      setOrderedMobileHomeSegments(mobileHomeSegments);
    }
  }, [mobileHomeSegments]);

  const {
    designConfigData,
    isLoading: isLoadingDesignConfig,
    mutateDesignConfig,
  } = useDesignConfigData();

  const handleSubmitSegmentOrder = (communitySettings: CommunitySettings) => {
    const segmentOrder = orderedMobileHomeSegments.map(
      (segment: MobileHomeSegment) => segment.kind,
    );
    const formattedPayload = {
      community_setting: {
        ...communitySettings,
        mobile_home_segments_order: segmentOrder,
      },
    };
    mutate(formattedPayload);
  };

  const handleSubmitBranding = (theme: BrandingSettings) => {
    const mappedData = {
      branded_mobile_app: {
        brand_color_light_mode: theme.brand_color_light_mode || "",
        brand_color_dark_mode: theme.brand_color_dark_mode || "",
        button_text_color_light: theme.button_text_color_light || "",
        button_text_color_dark: theme.button_text_color_dark || "",
        community_logo_light: theme.logo_light || "",
        community_logo_dark: theme.logo_dark || "",
      },
    };

    void mutateDesignConfig(mappedData);
  };

  // Don't render forms until we have data
  if (isLoadingDesignConfig || isLoading || isEmpty(designConfigData)) {
    return (
      <div className="mx-auto max-w-[784px] px-8 pb-8">
        <PageMetaData title={pageTitle} bodyClassNames="view-plans" />
        <SetLayoutConfig disableRightSidebar />
        <div className="mt-6">
          <SkeletonLoader variant="mobile-home-segments" />
        </div>
      </div>
    );
  }

  const designConfig = designConfigData?.config;
  const designTheme = designConfigData?.theme;
  const attachments = designConfigData?.attachments;

  const lightLogo = getInlineAttachmentData(
    designConfig?.community_logo_light?.signed_id,
    attachments,
  ) as InlineAttachment;
  const darkLogo = getInlineAttachmentData(
    designConfig?.community_logo_dark?.signed_id,
    attachments,
  ) as InlineAttachment;

  // Create default values object with just the branding fields we need
  const defaultValuesBranding = {
    brand_color_light_mode: designTheme?.brand_color_light_mode || "",
    brand_color_dark_mode: designTheme?.brand_color_dark_mode || "",
    button_text_color_light: designTheme?.button_text_color_light || "",
    button_text_color_dark: designTheme?.button_text_color_dark || "",
    logo_light: lightLogo?.signed_id || "",
    logo_dark: darkLogo?.signed_id || "",
  };

  return (
    <MobileHomeSegmentsProvider
      orderedMobileHomeSegments={orderedMobileHomeSegments}
      setOrderedMobileHomeSegments={setOrderedMobileHomeSegments}
    >
      <div className="mx-auto max-w-[784px] px-8 pb-8">
        <PageMetaData title={pageTitle} bodyClassNames="view-plans" />
        <SetLayoutConfig disableRightSidebar />

        {/* Navigation without onboarding carousel */}
        <div className="bg-primary border-primary mt-6 rounded-lg border px-8 py-7">
          <div className="flex">
            <div className="flex-1">
              <div className="border-primary flex justify-between">
                <Typography.TitleSm>
                  {t("settings_nav_v3.branded_apps_dashboard.navigation")}
                </Typography.TitleSm>
              </div>
            </div>
          </div>
          <FormDivider />
          <MobileNavigationForm
            defaultValues={defaultValues}
            handleSubmit={handleSubmitSegmentOrder}
            isSaving={isSaving}
          />

          <div className="mt-4 flex">
            <div className="flex-1">
              <div className="border-primary flex justify-between">
                <Typography.TitleSm>
                  {t("settings_nav_v3.branded_apps_dashboard.branding")}
                </Typography.TitleSm>
              </div>
            </div>
          </div>
          <FormDivider />
          <BrandingSettingsForm
            defaultValues={defaultValuesBranding}
            handleSubmit={handleSubmitBranding}
            isSaving={isSaving}
          />
        </div>
      </div>
    </MobileHomeSegmentsProvider>
  );
};
